import * as XLSX from "xlsx";
import Papa from 'papaparse';
import React, { Component } from 'react';
import { Button } from '../../../shared-components';
import PropTypes from 'prop-types';
import { t } from 'i18next';
import moment from 'moment';
import dateutil from 'dateutil';
import { 
  UPLOAD_FILES_MODAL_I18N_KEYS as MY_I18N_KEYS, 
  UPLOAD_FILES_MODAL_I18N_PREFIX as MY_I18N_PREFIX 
} from '../constants/i18n/UploadFilesModali18nConstants';
import {
  MAXIMUM_CONTENT_CHARACTERS,
  CONTENT_KEY,
  DATE_KEY,
  SOURCE_KEY,
  SOURCE_LINK_KEY,
  MAXIMUM_NUMBER_OF_ROWS_IN_UPLOADED_FILE,
  MAXIMUM_FILE_SIZE_IN_KB
} from '../constants/constants';

const FILE_TYPE_XLS                  = '.xls';
const FILE_TYPE_XLSX                 = '.xlsx';
const FILE_TYPE_CSV                  = '.csv';
const SPECIFIED_DATE_FORMAT_FOR_XLSX = 'd"/"m"/"yyyy H:mm:ss';
const EMPTY_STRING                   = "";
const COMMON_DATE_FORMATS = [
  'D/M/YYYY H:mm:ss',
  'D/M/YYYY H:mm',
  'D/M/YYYY',
  'D/M/YY H:mm:ss',
  'D/M/YY H:mm',
  'D/M/YY',
  'D-M-YYYY H:mm:ss',
  'D-M-YYYY H:mm',
  'D-M-YYYY',
  'D-M-YY'
];
const RELEVANT_COLUMNS = [CONTENT_KEY, DATE_KEY, SOURCE_KEY, SOURCE_LINK_KEY];


class UploadFileInstructionsSection extends Component {
  static propTypes = {
    handleFileValidation:                PropTypes.func.isRequired,
    handleFileUpload:                    PropTypes.func.isRequired
  };

  constructor(props) {
    super(props);
    this.uploadFileButtonRef = React.createRef();
  }

  getI18nText(textKey,params={}) {
    return t(MY_I18N_PREFIX + "." + textKey,params);
  }

  handleUploadFileButtonClick = e => {
    this.uploadFileButtonRef.current.click();
  }

  handleFileUploadChange = event => {
    const uploadedFile = event.target.files[0];
    this.readFile(uploadedFile); 
    this.props.handleFileValidation(uploadedFile.name);          
  };

  readFile = (uploadedFile) => {
    const fileName = uploadedFile.name.toLowerCase();
  
    if (fileName.endsWith(FILE_TYPE_XLSX) || fileName.endsWith(FILE_TYPE_XLS)) {
      const reader = new FileReader();
      reader.readAsArrayBuffer(uploadedFile);
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        this.parseExcelData(data, uploadedFile);
      };
    } else if (fileName.endsWith(FILE_TYPE_CSV)) {
      const reader = new FileReader();
      reader.readAsText(uploadedFile);
      reader.onload = (e) => {
        const csvData = e.target.result;
        this.parseCSVData(csvData, uploadedFile);
      };
    }
  };

  parseExcelData = (excelData, file) => {
    const workbook = XLSX.read(excelData, { type: "array", cellDates: true, raw: false, cellText:false});
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    const parsedData = XLSX.utils.sheet_to_json(sheet, { defval: EMPTY_STRING, raw: false, dateNF: SPECIFIED_DATE_FORMAT_FOR_XLSX});
    
    this.addRowNumberToParsedData(parsedData);
    
    const fileValidationErrors = this.validateDataAndFileSize(parsedData, file);
    const fileInfoAfterValidation = this.getfileInfoAfterValidation(parsedData, file);
    const totalDataPoints = parsedData.length;
    this.props.handleFileUpload({fileInfoAfterValidation, fileValidationErrors, parsedData, totalDataPoints});
  };

  parseCSVData = (csvData, file) => {
    Papa.parse(csvData, {
      header: true, 
      dynamicTyping: false, 
      skipEmptyLines: true,
      complete: (result) => {
        const parsedData = result.data;

        this.addRowNumberToParsedData(parsedData);
        const fileValidationErrors = this.validateDataAndFileSize(parsedData, file);
        const fileInfoAfterValidation = this.getfileInfoAfterValidation(parsedData, file);
        const totalDataPoints = parsedData.length;
        this.props.handleFileUpload({fileInfoAfterValidation, fileValidationErrors, parsedData, totalDataPoints});
      }
    });
  }

  addRowNumberToParsedData = (parsedData) => {
    parsedData.forEach((row, index) => {
      row.rowNumber = index + 1;
    });
  }

  validateDataAndFileSize = (parsedData, file) => {
    let errors = [];
    
    if (parsedData.length === 0) {
      errors.push({ row: 0, message: this.getI18nText(MY_I18N_KEYS.empty_file_error_message) });
      return errors;
    }
    else if (parsedData.length > MAXIMUM_NUMBER_OF_ROWS_IN_UPLOADED_FILE) {
      errors.push({ row: 0, message: this.getI18nText(MY_I18N_KEYS.exceeded_max_number_of_rows_error_message) });
      return errors;
    }

    if (this.isFileSizeGreaterThanMaximumSizeAccepted(file)) {
      errors.push({ row: 0, message: this.getI18nText(MY_I18N_KEYS.exceeded_max_file_size_error_message) });
      return errors;
    }
  
    const columnName = Object.keys(parsedData[0]).map(key => key);
    const hasContentColumn = columnName.includes(CONTENT_KEY);
    const hasDateColumn = columnName.includes(DATE_KEY);
    const hasSourceColumn = columnName.includes(SOURCE_KEY);
  
    errors = errors.concat(this.validateRequiredColumns(hasContentColumn));
  
    parsedData.forEach((row, index) => {
      const errorMessages = this.validateRow(row, hasContentColumn, hasDateColumn, hasSourceColumn);
  
      if (errorMessages.length > 0) {
        row.hasError = true;
        errors.push({ row: index + 1, message: errorMessages.join(" | ") });
      } else {
        row.hasError = false;
      }
    });
  
    return errors;
  };
  
  
  isFileSizeGreaterThanMaximumSizeAccepted = (file) => {
    let fileSizeInKB = this.convertFileSizeToKB(file.size);
    return (fileSizeInKB > MAXIMUM_FILE_SIZE_IN_KB);
  }
  
  validateRequiredColumns = (hasContentColumn) => {
    let columnErrors = [];
  
    if (!hasContentColumn) {
      columnErrors.push({ row: 0, message: this.getI18nText(MY_I18N_KEYS.no_content_column_error_message)});
    }
  
    return columnErrors;
  };

  validateRow = (row, hasContentColumn, hasDateColumn, hasSourceColumn) => {
    let errorMessages = [];
  
    if (hasContentColumn) {
      errorMessages = errorMessages.concat(this.validateContent(row));
    }
  
    if (hasDateColumn) {
      errorMessages = errorMessages.concat(this.validateDate(row));
    }
  
    if (hasSourceColumn) {
      errorMessages = errorMessages.concat(this.validateSource(row));
    }
  
    return errorMessages;
  };

  validateContent = (row) => {
    let contentErrorMessages = [];

    const content = row[CONTENT_KEY];
    if (!content || content.trim() === EMPTY_STRING) {
      contentErrorMessages.push(this.getI18nText(MY_I18N_KEYS.missing_data_in_content_column_error_message));
    } else if (content.length > MAXIMUM_CONTENT_CHARACTERS) {
      contentErrorMessages.push(this.getI18nText(MY_I18N_KEYS.content_column_exceeds_max_length_error_message));
    }

    return contentErrorMessages;
  };
  
  validateDate = (row) => {
    let dateErrorMessages = [];

    const date = row[DATE_KEY];
    if (!date || date.trim() === EMPTY_STRING) {
      dateErrorMessages.push(this.getI18nText(MY_I18N_KEYS.missing_data_in_date_column_error_message));
    } else if (!this.isValidDate(date)) {
      dateErrorMessages.push(this.getI18nText(MY_I18N_KEYS.invalid_date_error_message));
    }
  
    return dateErrorMessages;
  };
  
  validateSource = (row) => {
    let sourceErrorMessages = [];

    const source = row[SOURCE_KEY];
    if (!source || source.trim() === EMPTY_STRING) {
      sourceErrorMessages.push(this.getI18nText(MY_I18N_KEYS.missing_data_in_source_column_error_message));
    }
    if (source && source.includes(",")) {
      sourceErrorMessages.push(this.getI18nText(MY_I18N_KEYS.invalid_source_error_message));
    }
    if (source && source.length > MAXIMUM_CONTENT_CHARACTERS) {
      sourceErrorMessages.push(this.getI18nText(MY_I18N_KEYS.source_column_exceeds_max_length_error_message));
    }
  
    return sourceErrorMessages;
  };

  isDateValueNoneOrAnInteger = (dateValue) => {
    return (!isNaN(dateValue) && parseFloat(dateValue).toString() === dateValue);
  };

  canDateValueBeParsedByMoment = (dateValue) => {
    const parsedDate = moment(dateValue, COMMON_DATE_FORMATS, true);
    return parsedDate.isValid();
  };

  canDateValueBeParsedByDateUtil = (dateValue) => {
    const dateFromDateUtil = dateutil.parse(dateValue);
    return dateFromDateUtil && !isNaN(dateFromDateUtil.getTime());
  };

  isValidDate = (dateValue) => {
    if (this.isDateValueNoneOrAnInteger(dateValue)) {
      return false;
    }

    if (this.canDateValueBeParsedByMoment(dateValue)) {
      return true;
    }

    return this.canDateValueBeParsedByDateUtil(dateValue);
  };

  getfileInfoAfterValidation = (parsedData, file) => {
    const relevantColumns = RELEVANT_COLUMNS;
    const fileColumns = Object.keys(parsedData[0] || {})
      .map(key => key)
      .filter(key => relevantColumns.includes(key));
  
    return {
      name: file.name,
      size: this.convertFileSizeToKB(file.size),
      columns: fileColumns
    };
  };

  convertFileSizeToKB(fileSize) {
    let fileSizeInKB = (fileSize / 1024).toFixed(2);
    return fileSizeInKB;
  }


  render() {
    return (
        <div className='upload-files-instructions-container'>
        <div className='instructions-title'>
            <span>
            <i className="la la-lightbulb-o icon" />{this.getI18nText(MY_I18N_KEYS.instructions_title)}
            </span>
        </div>
        <ul className='instructions-text'>
            <li>{this.getI18nText(MY_I18N_KEYS.file_types_accepted_instructions)}</li>
            <li>{this.getI18nText(MY_I18N_KEYS.max_file_size_instructions)}</li>
            <li>{this.getI18nText(MY_I18N_KEYS.more_files_added_later_instructions)}</li>
        </ul>
        <div className='instructions-text link-to-formatting-instructions-container'>
            <span>
            <a href='https://ihpcscc.atlassian.net/wiki/spaces/RS/pages/2122317845/Pre-process+File+for+Uploading' target="_blank" rel="noopener noreferrer">{this.getI18nText(MY_I18N_KEYS.click_here_link_text)}</a>
            {this.getI18nText(MY_I18N_KEYS.click_for_fomatting_details_instructions)}
            </span>
        </div>
        <input 
            type="file" 
            accept=".xlsx, .xls, .csv" 
            onChange={this.handleFileUploadChange}
            style={{ display: 'none' }}
            ref={this.uploadFileButtonRef}
        />
        <Button
            label={this.getI18nText(MY_I18N_KEYS.upload_file_button_text)}
            icon="cloud-upload-alt"
            onClick={this.handleUploadFileButtonClick}
            inputClassName='upload-file-button btn-default'
        />
        </div>
    );
  }
}

export default UploadFileInstructionsSection;


// WEBPACK FOOTER //
// ./src/features/topics/components/file-upload/UploadFileInstructionsSection.js